import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';

import { additionalFeesService } from '@/services/providers';
import { planQueries } from '@/services/plans/queries';
import { showToast } from '@/utils/toast';
import { Plus, Edit, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

interface AdditionalFeesManagementProps {
  providerId: number;
  versionId: number;
  regionId: number;
}

interface AdditionalFeeData {
  id?: number;
  fee_type: string;
  custom_fee_type_name?: string | null;
  display_order?: number;
  internal_reference?: string | null;
  amount: number;
  description?: string | null;
  is_recurring: boolean | null;
  is_required: boolean | null;
}

interface Plan {
  id: number;
  name: string;
  channel_count?: number;
}

export function AdditionalFeesManagement({ 
  providerId, 
  versionId, 
  regionId 
}: AdditionalFeesManagementProps) {
  const [appliesTo, setAppliesTo] = useState<'provider' | 'plan' | 'category' | 'package'>('provider');
  const [selectedPlans, setSelectedPlans] = useState<number[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [providerFees, setProviderFees] = useState<AdditionalFeeData[]>([]);
  const [planFees, setPlanFees] = useState<Record<number, AdditionalFeeData[]>>({});

  const [providerTemplateSaved, setProviderTemplateSaved] = useState(false);
  const [planOverridesSaved, setPlanOverridesSaved] = useState<Record<number, boolean>>({});
  const [editingFee, setEditingFee] = useState<AdditionalFeeData | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPlanFee, setEditingPlanFee] = useState<{ planId: number; fee: AdditionalFeeData } | null>(null);
  const [showPlanFeeForm, setShowPlanFeeForm] = useState(false);

  // Load existing data
  useEffect(() => {
    loadData();
  }, [providerId, versionId, regionId]);

  // Load plan fees when plans are selected
  useEffect(() => {
    selectedPlans.forEach(planId => {
      if (!planFees[planId]) {
        loadPlanFees(planId);
      }
    });
  }, [selectedPlans]);

  // Auto-switch to plan mode when plan overrides exist (after refresh)
  useEffect(() => {
    if (Object.keys(planOverridesSaved).length > 0 && appliesTo === 'provider') {
      setAppliesTo('plan');
    }
  }, [planOverridesSaved, appliesTo]);

  // Copy provider fees to a selected plan (pre-fill like Promotional Offers)
  const copyProviderFeesToPlan = (planId: number) => {
    if (providerFees.length > 0 && !planFees[planId]) {
      const copiedFees = providerFees.map((fee) => ({
        ...fee,
        id: undefined
      }));
      setPlanFees((prev) => ({
        ...prev,
        [planId]: copiedFees
      }));
    }
  };

  const loadData = async () => {
    try {
      // Load existing provider fees
      const fees = await additionalFeesService.getAllFees(providerId.toString(), regionId.toString(), versionId.toString());
      if (fees && fees.length > 0) {
        setProviderFees(fees.map(fee => ({
          id: fee.id,
          fee_type: fee.fee_type,
          custom_fee_type_name: (fee as any).custom_fee_type_name,
          display_order: (fee as any).display_order,
          internal_reference: (fee as any).internal_reference,
          amount: fee.amount,
          description: fee.description,
          is_recurring: fee.is_recurring,
          is_required: fee.is_required
        })));
        setProviderTemplateSaved(true);
      }

      // Load plans
      const plansData = await planQueries.getPlans(providerId, versionId);
      setPlans(plansData || []);

      // Load existing plan overrides from override tables if any
      if (plansData && plansData.length > 0) {
        const planIds = plansData.map((plan: any) => plan.id);
        const planOverrides = await additionalFeesService.getPlanOverrides(planIds);
        
        if (planOverrides && planOverrides.length > 0) {
          // Group fees by plan
          const planFeesMap: Record<number, AdditionalFeeData[]> = {};
          planOverrides.forEach((fee: any) => {
            if (!planFeesMap[fee.plan_id]) {
              planFeesMap[fee.plan_id] = [];
            }
            planFeesMap[fee.plan_id].push({
              id: fee.id,
              fee_type: fee.fee_type,
              custom_fee_type_name: (fee as any).custom_fee_type_name,
              display_order: (fee as any).display_order,
              internal_reference: (fee as any).internal_reference,
              amount: fee.amount,
              description: fee.description,
              is_recurring: fee.is_recurring,
              is_required: fee.is_required
            });
          });
          
          setPlanFees(planFeesMap);
          
          // Mark plans with overrides as saved
          const savedPlans = [...new Set(planOverrides.map((override: any) => override.plan_id))] as number[];
          const savedState: Record<number, boolean> = {};
          savedPlans.forEach((planId: number) => {
            savedState[planId] = true;
          });
          setPlanOverridesSaved(savedState);
          
          // If there are plan overrides, select those plans but keep current mode
          if (savedPlans.length > 0) {
            setSelectedPlans(savedPlans);
          }
        }
      }

    } catch (error) {
      console.error('Error loading data:', error);
      toast.error("Failed to load existing data", {
        style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
      });
    }
  };

  // Validation function
  const validateFeeData = (data: AdditionalFeeData): string[] => {
    const errors: string[] = [];

    if (!data.fee_type) {
      errors.push("Fee type is required");
    }

    // Validate custom fee type name when "others" is selected
    if (data.fee_type === 'others' && (!data.custom_fee_type_name || data.custom_fee_type_name.trim() === '')) {
      errors.push("Please enter a custom fee type name when 'Others' is selected");
    }

    if (!data.amount || data.amount <= 0) {
      errors.push("Amount must be greater than 0");
    }

    return errors;
  };

  const handleAddFee = () => {
    setEditingFee({
      fee_type: '',
      amount: 0,
      description: '',
      is_recurring: false,
      is_required: true
    });
    setShowAddForm(true);
  };

  const handleEditFee = (fee: AdditionalFeeData) => {
    setEditingFee({ ...fee });
    setShowAddForm(true);
  };

  const handleDeleteFee = async (feeId: number) => {
    try {
      await additionalFeesService.deleteFee(feeId);
      setProviderFees(prev => prev.filter(fee => fee.id !== feeId));
      toast.success("Fee deleted successfully", {
        style: { backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }
      });
    } catch (error) {
      console.error('Error deleting fee:', error);
      toast.error("Failed to delete fee", {
        style: { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }
      });
    }
  };

  const handleSaveFee = async (feeData: AdditionalFeeData) => {
    // Validate data
    const errors = validateFeeData(feeData);
    if (errors.length > 0) {
      errors.forEach(error => showToast.error(error));
      return;
    }

    try {
      if (feeData.id) {
        // Update existing fee
        const updatedFee = await additionalFeesService.updateFee(feeData.id, {
          amount: feeData.amount,
          description: feeData.description,
          fee_type: feeData.fee_type,
          custom_fee_type_name: feeData.custom_fee_type_name,
          display_order: feeData.display_order,
          internal_reference: feeData.internal_reference,
          is_recurring: feeData.is_recurring ?? undefined,
          is_required: feeData.is_required ?? undefined
        });
        
        setProviderFees(prev => prev.map(fee => 
          fee.id === feeData.id ? { ...fee, ...updatedFee } : fee
        ));
        // setPlanFees(prev => prev.map(fee => 
        //   fee.id === feeData.id ? { ...fee, ...updatedFee } : fee
        // )); // This line was removed as per the edit hint
        showToast.updated('Fee');
      } else {
        // Create new fee
        const newFee = await additionalFeesService.createFee({
          provider_id: providerId,
          version_id: versionId,
          region_id: regionId,
          entity_type: 'provider',
          entity_id: null,
          fee_type: feeData.fee_type,
          custom_fee_type_name: feeData.custom_fee_type_name,
          display_order: feeData.display_order,
          internal_reference: feeData.internal_reference,
          amount: feeData.amount,
          description: feeData.description,
          is_recurring: feeData.is_recurring ?? undefined,
          is_required: feeData.is_required ?? undefined
        });
        
        setProviderFees(prev => [...prev, newFee]);
        // setPlanFees(prev => [...prev, newFee]); // This line was removed as per the edit hint
        showToast.success('Fee added successfully');
      }
      
      setShowAddForm(false);
      setEditingFee(null);
    } catch (error) {
      console.error('Error saving fee:', error);
      showToast.saveFailed('fee');
    }
  };

  const handleCancelEdit = () => {
    setShowAddForm(false);
    setEditingFee(null);
  };

  const handleAddPlanFee = (planId: number) => {
    setEditingPlanFee({
      planId,
      fee: {
        fee_type: '',
        amount: 0,
        description: '',
        is_recurring: false,
        is_required: true
      }
    });
    setShowPlanFeeForm(true);
  };

  const handleEditPlanFee = (planId: number, fee: AdditionalFeeData) => {
    setEditingPlanFee({ planId, fee: { ...fee } });
    setShowPlanFeeForm(true);
  };

  const handleDeletePlanFee = async (planId: number, fee: AdditionalFeeData) => {
    try {
      await additionalFeesService.deletePlanOverride(planId, fee.fee_type);
      setPlanFees(prev => ({
        ...prev,
        [planId]: prev[planId]?.filter(f => f.fee_type !== fee.fee_type) || []
      }));
      showToast.deleted('Plan fee');
    } catch (error) {
      console.error('Error deleting plan fee:', error);
      showToast.deleteFailed('plan fee');
    }
  };

  const handleSavePlanFee = async (planId: number, feeData: AdditionalFeeData) => {
    // Validate data
    const errors = validateFeeData(feeData);
    if (errors.length > 0) {
      errors.forEach(error => showToast.error(error));
      return;
    }

    try {
      // Use overrides table pattern (standardized, same as Promotional Offers)
      const result = await additionalFeesService.upsertPlanOverride(
        planId,
        {
          fee_type: feeData.fee_type,
          custom_fee_type_name: feeData.custom_fee_type_name,
          display_order: feeData.display_order,
          internal_reference: feeData.internal_reference,
          amount: feeData.amount,
          description: feeData.description,
          is_recurring: feeData.is_recurring ?? undefined,
          is_required: feeData.is_required ?? undefined
        }
      );

      // Merge into UI state by fee_type
      const existingIdx = (planFees[planId] || []).findIndex((f) => f.fee_type === feeData.fee_type);
      if (existingIdx >= 0) {
        setPlanFees((prev) => ({
          ...prev,
          [planId]: prev[planId]!.map((f, idx) => (idx === existingIdx ? { ...f, ...result } : f))
        }));
        showToast.updated('Plan fee');
      } else {
        setPlanFees((prev) => ({
          ...prev,
          [planId]: [...(prev[planId] || []), result]
        }));
        showToast.success('Plan fee added successfully');
      }
      
      setShowPlanFeeForm(false);
      setEditingPlanFee(null);
    } catch (error) {
      console.error('Error saving plan fee:', error);
      
      // Handle specific error cases
      if (error instanceof Error) {
        if (error.message.includes('duplicate key value violates unique constraint')) {
          showToast.warning("A fee of this type already exists for this plan. It has been updated instead.");
        } else if (error.message.includes('23505')) {
          showToast.constraintViolation('Plan fee');
        } else {
          showToast.error(error.message);
        }
      } else {
        showToast.saveFailed('plan fee');
      }
    }
  };

  const handleCancelPlanFeeEdit = () => {
    setShowPlanFeeForm(false);
    setEditingPlanFee(null);
  };

  const loadPlanFees = async (planId: number) => {
    try {
      const overrides = await additionalFeesService.getPlanOverrides([planId]);
      if (overrides && overrides.length > 0) {
        setPlanFees((prev) => ({
          ...prev,
          [planId]: overrides.map((o: any) => ({
            id: o.id,
            fee_type: o.fee_type,
            custom_fee_type_name: o.custom_fee_type_name,
            display_order: o.display_order,
            internal_reference: o.internal_reference,
            amount: o.amount,
            description: o.description,
            is_recurring: o.is_recurring,
            is_required: o.is_required
          }))
        }));
      }
    } catch (error) {
      console.error('Error loading plan fees:', error);
    }
  };

  const getFeeTypeLabel = (feeType: string, customTypeName?: string) => {
    const labels: Record<string, string> = {
      'setup': 'Setup Fee',
      'hidden': 'Hidden Fee',
      'equipment_rental': 'Equipment Rental',
      'early_termination': 'Early Termination',
      'installation': 'Installation Fee',
      'others': customTypeName || 'Others'
    };
    return labels[feeType] || feeType;
  };



  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Additional Fees Management</h2>
          <p className="text-muted-foreground">Configure additional fees and charges for this provider.</p>
        </div>
        <Badge variant="outline">
          {appliesTo === 'provider' ? 'Provider Template' : 'Plan Overrides'}
        </Badge>
      </div>

      {/* Applies To Selector */}
      <Card>
        <CardHeader>
          <CardTitle>Applies To</CardTitle>
        </CardHeader>
        <CardContent>
          <Select
            value={appliesTo}
            onValueChange={(value: 'provider' | 'plan' | 'category' | 'package') => setAppliesTo(value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="provider">Provider-wide</SelectItem>
              <SelectItem value="plan">Plan-wise</SelectItem>
              <SelectItem value="category">Category-wise</SelectItem>
              <SelectItem value="package">Package-wise</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Provider Template Component (Always shown) */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Provider Fees Configuration</CardTitle>
              <p className="text-sm text-muted-foreground">
                Configure all additional fees for this provider. Each fee type can be configured separately.
              </p>
            </div>
            {providerTemplateSaved && (
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                ✓ Saved
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Existing Fees List */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Current Fees</h3>
              <Button onClick={handleAddFee} size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Add Fee
              </Button>
            </div>

            {providerFees.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No fees configured yet.</p>
                <p className="text-sm">Click "Add Fee" to get started.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {providerFees.map((fee) => (
                  <div key={fee.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline">{getFeeTypeLabel(fee.fee_type, fee.custom_fee_type_name || undefined)}</Badge>
                          <span className="font-semibold">${fee.amount}</span>
                          {fee.is_recurring && <Badge variant="secondary">Recurring</Badge>}
                          {fee.is_required && <Badge variant="destructive">Required</Badge>}
                        </div>
                        {fee.description && (
                          <p className="text-sm text-muted-foreground">{fee.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditFee(fee)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fee.id && handleDeleteFee(fee.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add/Edit Fee Form */}
          {showAddForm && (
            <div className="mt-6 border rounded-lg p-4 bg-muted/50">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">
                  {editingFee?.id ? 'Edit Fee' : 'Add New Fee'}
                </h3>
                <Button variant="ghost" size="sm" onClick={handleCancelEdit}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <AdditionalFeeForm
                data={editingFee!}
                onChange={setEditingFee}
                onSave={handleSaveFee}
                onCancel={handleCancelEdit}
                disabled={false}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plan Template Component (Shown when plan-wise is selected OR when plan overrides exist) */}
      {(appliesTo === 'plan' || Object.keys(planOverridesSaved).length > 0) && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Plan Fee Overrides</CardTitle>
              <div className="flex gap-2">
                {selectedPlans.some(planId => planOverridesSaved[planId]) && (
                  <Badge variant="secondary" className="bg-green-100 text-green-800">
                    ✓ Overrides Saved
                  </Badge>
                )}
                {providerTemplateSaved && !selectedPlans.some(planId => planOverridesSaved[planId]) && (
                  <Badge variant="secondary">Editable</Badge>
                )}
                {!providerTemplateSaved && (
                  <Badge variant="outline">Read-only until provider template saved</Badge>
                )}
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              {providerTemplateSaved 
                ? 'Configure plan-specific fee overrides for selected plans.'
                : 'Save the provider template first to enable plan-specific overrides.'
              }
            </p>
            
            {/* Plan Selection Warning */}
            {selectedPlans.length === 0 && (
              <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span className="text-sm text-yellow-800 font-medium">
                    ⚠️ Please select at least one plan to configure plan-specific fee overrides
                  </span>
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {/* Plan Selection */}
            <div className="mb-6 space-y-2">
              <Label>Select Plans for Override</Label>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded-md p-3">
                {plans.length === 0 ? (
                  <p className="text-sm text-muted-foreground col-span-2">No plans available</p>
                ) : (
                  plans.map((plan) => (
                    <div key={plan.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`plan-${plan.id}`}
                        checked={selectedPlans.includes(plan.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedPlans([...selectedPlans, plan.id]);
                            // Pre-fill plan with provider fees when selected
                            copyProviderFeesToPlan(plan.id);
                          } else {
                            setSelectedPlans(selectedPlans.filter(id => id !== plan.id));
                          }
                        }}
                      />
                      <Label htmlFor={`plan-${plan.id}`} className="text-sm flex items-center gap-2">
                        {plan.name}
                        {planOverridesSaved[plan.id] && (
                          <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs">
                            ✓
                          </Badge>
                        )}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Plan Fee Overrides */}
            {selectedPlans.length > 0 ? (
              <div className="space-y-6">
                {selectedPlans.map(planId => {
                  const plan = plans.find(p => p.id === planId);
                  const planFeesList = planFees[planId] || [];
                  
                  return (
                    <div key={planId} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="text-lg font-semibold">{plan?.name || `Plan ${planId}`}</h4>
                          <p className="text-sm text-muted-foreground">
                            Plan-specific fee overrides
                          </p>
                        </div>
                        <Button 
                          onClick={() => handleAddPlanFee(planId)}
                          size="sm"
                          variant="outline"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Plan Fee
                        </Button>
                      </div>

                      {planFeesList.length === 0 ? (
                        <div className="text-center py-4 text-muted-foreground">
                          <p>No plan-specific fees configured.</p>
                          <p className="text-sm">Click "Add Plan Fee" to override provider fees for this plan.</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {planFeesList.map((fee) => (
                            <div key={fee.id} className="border rounded-lg p-3 bg-muted/30">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Badge variant="outline">{getFeeTypeLabel(fee.fee_type, fee.custom_fee_type_name || undefined)}</Badge>
                                    <span className="font-semibold">${fee.amount}</span>
                                    {fee.is_recurring && <Badge variant="secondary">Recurring</Badge>}
                                    {fee.is_required && <Badge variant="destructive">Required</Badge>}
                                    <Badge variant="secondary" className="bg-blue-100 text-blue-800">Plan Override</Badge>
                                  </div>
                                  {fee.description && (
                                    <p className="text-sm text-muted-foreground">{fee.description}</p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleEditPlanFee(planId, fee)}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDeletePlanFee(planId, fee)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>Select plans above to configure plan-specific fee overrides.</p>
              </div>
            )}

            {/* Plan Fee Form */}
            {showPlanFeeForm && editingPlanFee && (
              <div className="mt-6 border rounded-lg p-4 bg-muted/50">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">
                    {editingPlanFee.fee.id ? 'Edit Plan Fee' : 'Add Plan Fee'}
                  </h3>
                  <Button variant="ghost" size="sm" onClick={handleCancelPlanFeeEdit}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                <AdditionalFeeForm
                  data={editingPlanFee.fee}
                  onChange={(fee) => setEditingPlanFee({ ...editingPlanFee, fee })}
                  onSave={(fee) => handleSavePlanFee(editingPlanFee.planId, fee)}
                  onCancel={handleCancelPlanFeeEdit}
                  disabled={false}
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

interface AdditionalFeeFormProps {
  data: AdditionalFeeData;
  onChange: (data: AdditionalFeeData) => void;
  onSave: (data: AdditionalFeeData) => void;
  onCancel: () => void;
  disabled?: boolean;
}

function AdditionalFeeForm({ data, onChange, onSave, onCancel, disabled }: AdditionalFeeFormProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="fee_type">Fee Type</Label>
          <Select
            value={data.fee_type}
            onValueChange={(value) => onChange({ ...data, fee_type: value })}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select fee type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="setup">Setup Fee</SelectItem>
              <SelectItem value="hidden">Hidden Fee</SelectItem>
              <SelectItem value="equipment_rental">Equipment Rental</SelectItem>
              <SelectItem value="early_termination">Early Termination</SelectItem>
              <SelectItem value="installation">Installation Fee</SelectItem>
              <SelectItem value="others">Others (Custom)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="amount">Amount ($)</Label>
          <Input
            type="number"
            step="0.01"
            value={data.amount}
            onChange={(e) => onChange({ 
              ...data, 
              amount: e.target.value ? parseFloat(e.target.value) : 0 
            })}
            disabled={disabled}
            placeholder="0.00"
          />
        </div>
      </div>

      {/* Custom Fee Type Name - shown only when "others" is selected */}
      {data.fee_type === 'others' && (
        <div className="space-y-2">
          <Label htmlFor="custom_fee_type_name">Custom Fee Type Name</Label>
          <Input
            id="custom_fee_type_name"
            value={data.custom_fee_type_name || ''}
            onChange={(e) => onChange({ ...data, custom_fee_type_name: e.target.value })}
            disabled={disabled}
            placeholder="Enter custom fee type name (e.g., Activation, Processing, etc.)"
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          value={data.description || ''}
          onChange={(e) => onChange({ ...data, description: e.target.value })}
          disabled={disabled}
          placeholder="Describe the fee..."
          rows={3}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex items-center space-x-2">
          <Switch
            checked={data.is_recurring || false}
            onCheckedChange={(checked) => onChange({ ...data, is_recurring: checked })}
            disabled={disabled}
          />
          <Label>Recurring Fee</Label>
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            checked={data.is_required || false}
            onCheckedChange={(checked) => onChange({ ...data, is_required: checked })}
            disabled={disabled}
          />
          <Label>Required Fee</Label>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={() => onSave(data)} disabled={disabled}>
          {data.id ? 'Update Fee' : 'Add Fee'}
        </Button>
      </div>
    </div>
  );
}